/**
 * usePlan — fetches subscription plan + credit balance for the current org.
 * Cached in memory for 60s to avoid hammering the API on every navigation.
 */
import { ref, computed } from 'vue'
import api from '@/lib/api'

export interface PlanInfo {
  plan: string          // 'free' | 'pro' | 'business' | 'team'
  plan_name: string
  status: string        // 'active' | 'trialing' | 'canceled' etc.
  credits_remaining: number
  credits_used_this_month: number
  bonus_credits: number
  monthly_credits: number
  ai_credits: number
  branding: boolean
  watermark: boolean
}

const cache = ref<PlanInfo | null>(null)
const cacheTs = ref(0)
const TTL_MS = 60_000   // 1 minute

/** Call on logout so the next user doesn't see stale plan data */
export function clearPlanCache() {
  cache.value = null
  cacheTs.value = 0
}

export function usePlan() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isFree = computed(() => !cache.value || cache.value.plan === 'free')
  const isPaid = computed(() => !!cache.value && cache.value.plan !== 'free')
  const creditsRemaining = computed(() => cache.value?.credits_remaining ?? 0)
  const planName = computed(() => cache.value?.plan_name ?? 'Free')
  const plan = computed(() => cache.value?.plan ?? 'free')

  async function fetchPlan(force = false) {
    if (!force && cache.value && Date.now() - cacheTs.value < TTL_MS) return
    loading.value = true
    error.value = null
    try {
      const data = await api.get<PlanInfo>('/api/users/subscription')
      cache.value = data
      cacheTs.value = Date.now()
    } catch (e: any) {
      error.value = e?.message || 'Failed to load plan'
    } finally {
      loading.value = false
    }
  }

  function invalidate() {
    cacheTs.value = 0
  }

  return {
    plan,
    planName,
    isFree,
    isPaid,
    creditsRemaining,
    planInfo: cache,
    loading,
    error,
    fetchPlan,
    invalidate,
  }
}
